import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faEye, faFile } from "@fortawesome/pro-regular-svg-icons";
import {
    faExclamationCircle,
    faEyeSlash,
    faFileMinus,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { filter, isEmpty, some } from "lodash";
import { PaymentRequestStatus } from "../../../../PaymentContainerClient/commons/enums";
import DocumentDto from "../../../../PaymentContainerClient/dtos/DocumentDto";
import PaymentRequestDto from "../../../../PaymentContainerClient/dtos/PaymentRequestDto";
import getFormattedMessageWithScope from "../../../../utils/getFormattedMessageWithScope";
import getCreditorName from "../../../commons/getCreditorName";
import { DocumentsPreviewer } from "../../DocumentsPreviewer";
import { PrivacyCookiePolicyMenu } from "../../PrivacyCookiePolicyMenu";
import { useWindowDimensions } from "../../WindowDimensionsProvider";
import "./style.css";

const fm = getFormattedMessageWithScope(
    "components.PaymentForm.DocumentsPreview"
);

interface DocumentsPreviewProps {
    paymentRequest: PaymentRequestDto;
    onOpenDocumentsPreviewer: Function;
    isDownloadingDocument: boolean;
    isDocumentsPreviewerOpen: boolean;
    onCloseDocumentsPreviewer: Function;
    onDownloadDocument: Function;
    documentIdDownloading?: string;
    selectedDocumentForPreview?: DocumentDto;
}

export const DocumentsPreview = ({
    paymentRequest,
    onOpenDocumentsPreviewer,
    isDownloadingDocument,
    isDocumentsPreviewerOpen,
    onCloseDocumentsPreviewer,
    onDownloadDocument,
    documentIdDownloading,
    selectedDocumentForPreview,
}: DocumentsPreviewProps) => {
    const { width } = useWindowDimensions();

    function renderSmartphoneDocumentCard(doc: DocumentDto) {
        const isBlockedBoundToPaymentDocument =
            !doc.allowDownloadBeforePayment &&
            paymentRequest.status !== PaymentRequestStatus.Paid;
        return (
            <div
                key={doc.documentId}
                className={classNames(
                    "c-DocumentsPreview-documents-document",
                    isBlockedBoundToPaymentDocument
                        ? "c-DocumentsPreview-documents-document-disabled"
                        : undefined
                )}
            >
                <div className="c-DocumentsPreview-documents-document-info">
                    <FontAwesomeIcon
                        icon={faFile as IconProp}
                        className="c-DocumentsPreview-documents-document-info-icon"
                    />
                    <span>{doc.description}</span>
                </div>
                <div className="c-DocumentsPreview-documents-document-action">
                    <div className="c-DocumentsPreview-documents-document-action-icon-container">
                        <FontAwesomeIcon
                            icon={faEye as IconProp}
                            className={classNames(
                                "c-DocumentsPreview-documents-document-action-icon",
                                isBlockedBoundToPaymentDocument
                                    ? "c-DocumentsPreview-documents-document-action-icon-disabled"
                                    : undefined
                            )}
                            onClick={() => {
                                if (!isBlockedBoundToPaymentDocument)
                                    onOpenDocumentsPreviewer(doc);
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    const someDocumentsAreBlockedBoundToPayment =
        paymentRequest.status !== PaymentRequestStatus.Paid &&
        some(
            paymentRequest.paymentDocuments,
            (doc) => !doc.allowDownloadBeforePayment
        ) &&
        !isEmpty(paymentRequest.paymentDocuments);

    const allDocumentsAreBlockedBoundToPayment =
        paymentRequest.status !== PaymentRequestStatus.Paid &&
        !some(
            paymentRequest.paymentDocuments,
            (doc) => doc.allowDownloadBeforePayment
        ) &&
        !isEmpty(paymentRequest.paymentDocuments);

    return (
        <>
            <div className="c-PaymentForm-form-title">
                {fm("title")} <PrivacyCookiePolicyMenu />
            </div>
            {paymentRequest.creditor.name || paymentRequest.creditor.holder ? (
                <div className="c-DocumentsPreview-request-creditor">
                    <span className="c-DocumentsPreview-request-creditor-name">
                        {getCreditorName(paymentRequest.creditor)}
                    </span>
                    <span className="c-DocumentsPreview-request-creditor-message">
                        {" "}
                        {fm("sentYouTheDocument")}
                    </span>
                </div>
            ) : undefined}
            {isEmpty(paymentRequest.paymentDocuments) ? (
                <div className="c-DocumentsPreview-noDocuments">
                    <div className="c-DocumentsPreview-noDocuments-icon-container">
                        <FontAwesomeIcon
                            icon={faFileMinus as IconProp}
                            className="c-DocumentsPreview-noDocuments-icon"
                        />
                    </div>
                    <p className="c-DocumentsPreview-noDocuments-info-line1">
                        {fm("noAttachedDocuments")}
                    </p>
                </div>
            ) : allDocumentsAreBlockedBoundToPayment && width > 1000 ? (
                <div className="c-DocumentsPreview-noDocumentsAvailable">
                    <div className="c-DocumentsPreview-noDocumentsAvailable-icon-container">
                        <FontAwesomeIcon
                            icon={faEyeSlash as IconProp}
                            className="c-DocumentsPreview-noDocumentsAvailable-icon"
                        />
                    </div>
                    <p className="c-DocumentsPreview-noDocumentsAvailable-info-line1">
                        {fm("noDocumentsPreviewAvailable")}
                    </p>
                    <p className="c-DocumentsPreview-noDocumentsAvailable-info-line2">
                        {fm("payTheRequestToSeeDocuments")}
                    </p>
                </div>
            ) : (
                <div className="c-DocumentsPreview-documentsPreviewer">
                    {someDocumentsAreBlockedBoundToPayment ? (
                        <div className="c-DocumentsPreview-documentsPreviewer-someDocumentsBoundToPayment">
                            <div className="c-DocumentsPreview-documentsPreviewer-someDocumentsBoundToPayment-icon-container">
                                <FontAwesomeIcon
                                    icon={faExclamationCircle as IconProp}
                                    className="c-DocumentsPreview-documentsPreviewer-someDocumentsBoundToPayment-icon"
                                />
                            </div>
                            <span className="c-DocumentsPreview-documentsPreviewer-someDocumentsBoundToPayment-text">
                                {fm("proceedWithPaymentToViewBlockedDocuments")}
                            </span>
                        </div>
                    ) : undefined}

                    <DocumentsPreviewer
                        isOpen={isDocumentsPreviewerOpen}
                        onClose={onCloseDocumentsPreviewer}
                        documents={paymentRequest.paymentDocuments}
                        documentsAvailableForTheUser={
                            paymentRequest.status === PaymentRequestStatus.Paid
                                ? paymentRequest.paymentDocuments
                                : filter(paymentRequest.paymentDocuments, {
                                      allowDownloadBeforePayment: true,
                                  })
                        }
                        onDownload={onDownloadDocument}
                        isDownloading={isDownloadingDocument}
                        paymentRequest={paymentRequest}
                        documentIdDownloading={documentIdDownloading}
                        defaultSelectedDocument={selectedDocumentForPreview}
                    />
                </div>
            )}
            {width <= 1000 ? (
                <div className="c-DocumentsPreview-documents">
                    {paymentRequest.paymentDocuments.map((doc) =>
                        renderSmartphoneDocumentCard(doc)
                    )}
                </div>
            ) : undefined}
        </>
    );
};
